/**
 * Canonical standard codes (SSOT) — keep aligned with backend/utils/standardNormalizer.js
 */
const ALIAS_MAP: Record<string, string> = {
  // ANSSI / French national → ENS_ANSSI
  ANSSI_FR: "ENS_ANSSI",
  ANSSI: "ENS_ANSSI",
  "ANSSI FR": "ENS_ANSSI",
  NATIONAL_ANSSI_FR: "ENS_ANSSI",

  // Technical Controls SSOT
  TECHNICAL_CONTROLS: "TECH_CONTROLS",
  "TECHNICAL CONTROLS": "TECH_CONTROLS",
  "TECH CONTROLS": "TECH_CONTROLS",
  TECH_CONTROLS: "TECH_CONTROLS",

  // SDLC
  "SDLC SECURITY": "SDLC_SECURITY",
  SDLC_SECURITY: "SDLC_SECURITY",
  SECURE_SDLC: "SDLC_SECURITY",

  // NIS2
  NIS2_FULL: "NIS2",
  NIS2_FULL_2024: "NIS2",
  "NIS2-FULL": "NIS2",
  "NIS2 FULL": "NIS2",
  "NIS 2": "NIS2",

  // CIS
  CIS_V8: "CIS",
  "CIS CONTROLS V8": "CIS",
  CIS_CONTROLS: "CIS",
  CIS_CONTROLS_V8: "CIS",
  CISCONTROLSV8: "CIS",
  "CIS CONTROLS": "CIS",

  // SOC2 / ISO / PCI / SWIFT
  "SOC 2": "SOC2",
  SOC_2: "SOC2",
  "ISO/IEC 27001": "ISO27001",
  ISO_27001: "ISO27001",
  "ISO 27001": "ISO27001",
  "ISO/IEC 42001": "ISO42001",
  "ISO/IEC 42001:2023": "ISO42001",
  ISO_42001: "ISO42001",
  "ISO 42001": "ISO42001",
  ISO42001_2023: "ISO42001",
  "PCI DSS 2025": "PCI_DSS_2025",
  PCI_DSS: "PCI_DSS_2025",
  "SWIFT CSCF": "SWIFT_CSCF_2025",
  "SWIFT CSCF 2025": "SWIFT_CSCF_2025",
  SWIFT_CSCF_2025: "SWIFT_CSCF_2025",
  "SWIFT CSCF/SCP": "SWIFT_CSCF_2025",
  "SWIFT SCP": "SWIFT_CSCF_2025",

  // ENS Spain + national profiles
  "ENS_ES/NSE": "ENS_ES",
  NSE: "ENS_ES",
  ENS: "ENS_ES",
  NATIONAL_ENS_ES: "ENS_ES",
  NATIONAL_BSI_DE: "BSI_DE",
  NATIONAL_KSBP_LT: "KSBP_LT",
  NATIONAL_NCA_EE: "NCA_EE",
};

const COMPACT_ALIASES: Record<string, string> = {
  SDLCSECURITY: "SDLC_SECURITY",
  TECHNICALCONTROLS: "TECH_CONTROLS",
  TECHCONTROLS: "TECH_CONTROLS",
  SWIFTCSCF2025: "SWIFT_CSCF_2025",
  PCIDSS2025: "PCI_DSS_2025",
  CISCONTROLSV8: "CIS",
  ISOIEC27001: "ISO27001",
  ISOIEC42001: "ISO42001",
  ISOIEC420012023: "ISO42001",
  ANSSIFR: "ENS_ANSSI",
};

function normalizeInput(code: string | null | undefined): string {
  return String(code ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s*\/\s*/g, "/")
    .replace(/-/g, "_")
    .replace(/\s+/g, " ")
    .trim();
}

function underscoreKey(normalizedSpaced: string): string {
  return normalizedSpaced.replace(/\s+/g, "_").replace(/_+/g, "_");
}

export function normalizeStandardCode(code: string | null | undefined): string {
  const spaced = normalizeInput(code);
  if (!spaced) return "";

  if (ALIAS_MAP[spaced]) return ALIAS_MAP[spaced];

  const underscored = underscoreKey(spaced);
  if (ALIAS_MAP[underscored]) return ALIAS_MAP[underscored];

  const compact = spaced.replace(/[\s_]+/g, "");
  if (COMPACT_ALIASES[compact]) return COMPACT_ALIASES[compact];

  return underscored;
}

export function isSameStandard(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  const l = normalizeStandardCode(left);
  const r = normalizeStandardCode(right);
  return !!l && !!r && l === r;
}

export function getCanonicalStandardCode(code: string | null | undefined): string {
  return normalizeStandardCode(code);
}

export { ALIAS_MAP as STANDARD_ALIAS_MAP };
